import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/index";
import { useOutletContext } from "react-router-dom";
import { useCancelForm } from "@context/cancel-form-context";
import { CreateInsuranceFields, ShippingCaseInsurances } from "./types";
import { ShippingCaseResponse } from "@pages/shippingView/types";
import { Add, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  fetchInsurance,
  createInsurance,
  removeInsurance,
  updateInsurance,
  hideWarning,
} from "./insuranceSlice";
import BlankShippingCase from "@pages/shippingView/components/blankShippingCase";
import CreateInsurance from "./components/createInsurance";
import Warning from "@atoms/warning";
import RemoveDialog from "@pages/shippingView/components/removeDialog";
import InsuranceAccordion from "./components/insuranceAccordion";
import CancelDialog from "@pages/shippingView/components/cancelDialog";
import { canEditInsurance } from "@pages/shippingView/helpers/tabs-mapping";

const Insurance: React.FC = () => {
  const dispatch = useAppDispatch();
  const accordionRef = useRef<HTMLDivElement>(null);

  const [insurances, setInsurances] = useState<ShippingCaseInsurances>([]);
  const [expandedAccordions, setExpandedAccordions] = useState<number[]>([]);
  const [editId, setEditId] = useState<number | null>(null);
  const [editData, setEditData] =
    useState<Partial<CreateInsuranceFields> | null>(null);
  const [hasNewInsurance, setHasNewInsurance] = useState(false);
  const [newInsuranceData, setNewInsuranceData] =
    useState<Partial<CreateInsuranceFields> | null>(null);
  const [lastEditedId, setLastEditedId] = useState<number | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState<number | null>(null);
  const { allowNavigation } = useCancelForm();
  const { shipping } = useOutletContext<{ shipping: ShippingCaseResponse }>();
  const { loading, items, success, error } = useSelector(
    (state: RootState) => state.viewShipping.insurance,
  );
  const role = useSelector((state: RootState) => state.auth.user?.role) || "";
  const isPermitted = canEditInsurance(role);

  useEffect(() => {
    setInsurances(items);
    if (lastEditedId !== null) {
      setExpandedAccordions([lastEditedId]);
      setLastEditedId(null);
    } else if (hasNewInsurance) {
      const newItem = items[items.length - 1];
      if (newItem?.id) {
        setExpandedAccordions([newItem.id]);
      }
      setHasNewInsurance(false);
    } else if (editId === null && items?.length) {
      setExpandedAccordions([items[0].id]);
    }
  }, [items]);

  const {
    setIsDirty,
    isDirty,
    setPromptElement,
    hidePrompt,
    showPrompt,
    setActiveScope,
  } = useCancelForm();

  useEffect(() => {
    setPromptElement?.(
      <CancelDialog
        type="insurance"
        edit={!hasNewInsurance}
        handleClose={() => {
          hidePrompt("insurance");
        }}
        handleCancel={() => {
          hidePrompt("insurance");
        }}
      />,
      "insurance",
    );
  }, [setPromptElement, hidePrompt, hasNewInsurance]);

  useEffect(() => {
    dispatch(fetchInsurance(shipping.id));
  }, []);

  const toggleAccordion = (id: number) => {
    setExpandedAccordions((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleEdit = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number,
  ) => {
    e.stopPropagation();
    setEditId(id);
    const item = insurances.find((ins) => ins.id === id);
    if (item) setEditData(item);
    setExpandedAccordions((prev) => [...new Set([...prev, id])]);
  };

  const updateFields = (values: Partial<CreateInsuranceFields>) => {
    setActiveScope("insurance");
    setIsDirty(true);
    if (editId !== null) {
      setEditData((prev) => ({
        ...(prev ?? {}),
        ...values,
      }));
    } else if (hasNewInsurance) {
      setNewInsuranceData((prev) => ({
        ...(prev ?? {}),
        ...values,
      }));
    }
  };

  const handleRemoveInsurance = (id: number) => {
    dispatch(removeInsurance(shipping.id, id));
    setRemoveDialogOpen(null);
  };

  const handleSave = () => {
    allowNavigation?.();
    if (editId !== null && editData) {
      dispatch(
        updateInsurance(editData as CreateInsuranceFields, shipping.id, editId),
      );
    } else if (hasNewInsurance && newInsuranceData) {
      dispatch(
        createInsurance(newInsuranceData as CreateInsuranceFields, shipping.id),
      );
      setNewInsuranceData(null);
    }
    if (editId !== null && typeof editId === "number") {
      setLastEditedId(editId);
    }

    setEditId(null);
    setEditData(null);
  };

  const handleCancel = () => {
    if (isDirty) {
      showPrompt(
        () => {
          if (hasNewInsurance) {
            setHasNewInsurance(false);
            setNewInsuranceData(null);
          } else {
            setEditId(null);
            setEditData(null);
          }
          setIsDirty(false);
          hidePrompt("insurance");
        },
        () => {
          hidePrompt("insurance");
        },
        "insurance",
      );
    } else {
      if (hasNewInsurance) {
        setHasNewInsurance(false);
        setNewInsuranceData(null);
      } else {
        setEditId(null);
        setEditData(null);
      }
      setIsDirty(false);
    }
  };

  const handleAdd = () => {
    setHasNewInsurance(true);
    setNewInsuranceData({});
    setTimeout(() => {
      accordionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  };

  const closeCallback = () => dispatch(hideWarning());

  return (
    <>
      {!!error && (
        <Warning text={error} severity="error" closeCallback={closeCallback} />
      )}
      {!!success && (
        <Warning
          text={success}
          severity="success"
          closeCallback={closeCallback}
        />
      )}
      {loading && <CircularProgress />}
      {!loading &&
        ((!items || !items.length) && !hasNewInsurance ? (
          <BlankShippingCase
            type="insurance"
            text="No insurance information found."
            subtext="To get started, please click the button below."
            isPermitted={isPermitted}
            init={handleAdd}
          />
        ) : (
          <Stack gap={1}>
            {insurances?.map((insurance, index) => (
              <InsuranceAccordion
                key={insurance.id}
                isPermitted={isPermitted}
                insurance={insurance}
                index={index}
                expandedAccordions={expandedAccordions}
                toggleAccordion={toggleAccordion}
                editId={editId}
                handleEdit={handleEdit}
                hasNewInsurance={hasNewInsurance}
                setRemoveDialogOpen={setRemoveDialogOpen}
                editData={editData}
                updateFields={updateFields}
                setEditId={setEditId}
                setEditData={setEditData}
                handleSave={handleSave}
                handleCancel={handleCancel}
              />
            ))}
            {hasNewInsurance && (
              <Accordion
                ref={accordionRef}
                key="new"
                defaultExpanded={true}
                sx={{
                  boxShadow: "none",
                  p: 1,
                  backgroundColor: "rgba(250, 250, 250, 1)",
                }}
                className="scInsurance"
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ width: "calc(100% - 50px)" }}>
                    <Typography sx={{ fontSize: "16px" }}>
                      {`#${insurances.length + 1} Insurance`}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <>
                    <CreateInsurance
                      fields={newInsuranceData}
                      updateFields={updateFields}
                    />
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => handleCancel()}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => handleSave()}
                      >
                        {typeof editId !== "number"
                          ? "Create Insurance"
                          : "Save"}
                      </Button>
                    </Box>
                  </>
                </AccordionDetails>
              </Accordion>
            )}
            {isPermitted && (
              <Box sx={{ p: "8px 0 0" }}>
                <Button
                  startIcon={<Add />}
                  disabled={hasNewInsurance || !!editData}
                  onClick={() => handleAdd()}
                >
                  Add insurance
                </Button>
              </Box>
            )}
          </Stack>
        ))}
      {removeDialogOpen && (
        <RemoveDialog
          handleClose={() => setRemoveDialogOpen(null)}
          handleRemove={() => handleRemoveInsurance(removeDialogOpen)}
          type="insurance"
        />
      )}
    </>
  );
};

export default Insurance;
